import React, { useMemo, useState, useEffect } from 'react';
import { Gift } from 'lucide-react';
import {
  format,
  startOfWeek,
  addDays,
  isToday,
  isSameDay,
  getWeek
} from 'date-fns';
import { de } from 'date-fns/locale';
import { expandEvents } from '../utils/recurrence';

const Dashboard = ({ data }) => {
  const today = new Date();
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);
  const weekStart = useMemo(() => startOfWeek(today, { weekStartsOn: 1 }), []);
  const week = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const calendarEvents = data.calendar?.events || [];
  const categories = data.calendar?.categories || [];
  const birthdaySettings = data.calendar?.birthdaySettings || { linkedToCalendar: false, categoryId: 'purple' };
  const birthdays = data.calendar?.birthdays || [];

  // Generate virtual birthday events (mirror Calendar.js)
  const birthdayEvents = useMemo(() => {
    if (!birthdaySettings.linkedToCalendar) return [];
    return birthdays
      .map((p) => {
        let day = p.day, month = p.month, year = p.year;
        if (!day && p.birthDate) {
          const parts = p.birthDate.split('-').map(Number);
          year = parts[0]; month = parts[1]; day = parts[2];
        }
        if (!day || !month) return null;
        const curYear = today.getFullYear();
        const dateStr = `${curYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const nextAge = year ? curYear - year : null;
        const ageLabel = nextAge ? ` (${nextAge})` : '';
        return {
          id: `birthday-${p.id}`,
          title: `${p.name}${ageLabel}`,
          date: dateStr,
          endDate: dateStr,
          allDay: false,
          startTime: '',
          endTime: '',
          categoryId: birthdaySettings.categoryId,
          isBirthday: true
        };
      })
      .filter(Boolean);
  }, [birthdays, birthdaySettings.linkedToCalendar, birthdaySettings.categoryId]);

  const expandedCalendarEvents = useMemo(
    () => expandEvents(calendarEvents, addDays(weekStart, -7), addDays(weekStart, 14)),
    [calendarEvents, weekStart]
  );
  const events = useMemo(
    () => [...expandedCalendarEvents, ...birthdayEvents],
    [expandedCalendarEvents, birthdayEvents]
  );

  // Apply birthday-label override (treat linked category as filled "Birthday")
  const displayCategories = useMemo(() => {
    if (!birthdaySettings.linkedToCalendar) return categories;
    return categories.map((c) =>
      c.id === birthdaySettings.categoryId
        ? { ...c, name: 'Birthday', isCustom: true }
        : c
    );
  }, [categories, birthdaySettings.linkedToCalendar, birthdaySettings.categoryId]);

  const getCategory = (id) => displayCategories.find((c) => c.id === id) || displayCategories[0];

  const getEventsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events
      .filter((ev) => {
        if (ev.endDate && ev.endDate !== ev.date) {
          return dateStr >= ev.date && dateStr <= ev.endDate;
        }
        return ev.date === dateStr;
      })
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  };

  // Multi-day all-day bars across the week, with stacking
  const multiDayBars = useMemo(() => {
    const weekStartStr = format(week[0], 'yyyy-MM-dd');
    const weekEndStr = format(week[6], 'yyyy-MM-dd');

    const candidates = events
      .filter(
        (ev) =>
          ev.allDay !== false &&
          ev.endDate &&
          ev.endDate !== ev.date &&
          ev.date <= weekEndStr &&
          ev.endDate >= weekStartStr
      )
      .map((ev) => {
        const startIdx = week.findIndex((d) => format(d, 'yyyy-MM-dd') >= ev.date);
        const endIdxRev = [...week].reverse().findIndex((d) => format(d, 'yyyy-MM-dd') <= ev.endDate);
        const endIdx = 6 - endIdxRev;
        return {
          event: ev,
          startCol: Math.max(0, startIdx),
          endCol: Math.min(6, endIdx),
          continuesLeft: ev.date < weekStartStr,
          continuesRight: ev.endDate > weekEndStr
        };
      })
      .sort((a, b) => {
        const lenA = a.endCol - a.startCol;
        const lenB = b.endCol - b.startCol;
        if (lenB !== lenA) return lenB - lenA;
        return a.startCol - b.startCol;
      });

    const rowEnds = [];
    return candidates.map((bar) => {
      let row = 0;
      while (rowEnds[row] !== undefined && rowEnds[row] >= bar.startCol) row++;
      rowEnds[row] = bar.endCol;
      return { ...bar, row };
    });
  }, [week, events]);

  const multiDayIds = new Set(multiDayBars.map((b) => b.event.id));
  const barRowCount = multiDayBars.reduce((m, b) => Math.max(m, b.row + 1), 0);
  const BAR_HEIGHT = 20;
  const BAR_TOP_OFFSET = 32;
  const reservedSpace = barRowCount * BAR_HEIGHT;

  const weekdayLabels = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const monthLabel = format(week[0], 'MMMM yyyy', { locale: de });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <p className="text-gray-500">Welcome back! Here's your life overview.</p>
      </div>

      {/* This Week (calendar widget) — edge-to-edge on mobile. */}
      <div className="bg-white border-y sm:border sm:rounded-xl border-gray-200 overflow-hidden -mx-4 sm:mx-0">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Diese Woche</h3>
          <span className="text-xs text-gray-500 capitalize">{monthLabel}</span>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-[repeat(7,minmax(0,1fr))] sm:grid-cols-[50px_repeat(7,minmax(0,1fr))] border-b border-gray-200 bg-gray-50">
          <div className="hidden sm:block p-2 text-xs font-medium text-gray-400 text-center">KW</div>
          {weekdayLabels.map((lbl, i) => (
            <div
              key={lbl}
              className={`p-1.5 sm:p-2 text-[10px] sm:text-xs font-medium text-center uppercase tracking-wider ${
                i >= 5 ? 'text-red-500' : 'text-gray-600'
              }`}
            >
              {lbl}
            </div>
          ))}
        </div>

        {/* Single week row */}
        <div
          className="grid grid-cols-[repeat(7,minmax(0,1fr))] sm:grid-cols-[50px_repeat(7,minmax(0,1fr))] min-h-[100px] sm:min-h-[120px] relative"
        >
          {/* Week number — desktop only */}
          <div className="hidden sm:flex items-start justify-center pt-2 bg-gray-50 text-xs font-medium text-gray-400">
            {getWeek(week[0], { weekStartsOn: 1, firstWeekContainsDate: 4 })}
          </div>

          {week.map((day) => {
            const todayFlag = isToday(day);
            const weekend = day.getDay() === 0 || day.getDay() === 6;
            const allDayEvents = getEventsForDay(day).filter((ev) => !multiDayIds.has(ev.id));
            const birthdayEventsForDay = allDayEvents.filter((ev) => ev.isBirthday);
            const dayEvents = allDayEvents.filter((ev) => !ev.isBirthday);
            const firstBirthday = birthdayEventsForDay[0];
            const firstBirthdayCat = firstBirthday ? getCategory(firstBirthday.categoryId) : null;

            return (
              <div
                key={day.toISOString()}
                className="border-l border-gray-200 p-1 sm:p-1.5 min-w-0 overflow-hidden"
              >
                <div className="flex items-center gap-1 mb-1 min-w-0">
                  <span
                    className={`flex-shrink-0 inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 text-[11px] sm:text-xs font-medium rounded-full ${
                      todayFlag
                        ? 'bg-primary-600 text-white'
                        : weekend
                        ? 'text-red-500'
                        : 'text-gray-700'
                    }`}
                  >
                    {format(day, 'd')}
                  </span>

                  {firstBirthday && firstBirthdayCat && (
                    <div
                      className="flex-1 min-w-0 text-xs px-1.5 py-0.5 rounded font-semibold flex items-center gap-1"
                      style={{
                        backgroundColor: `${firstBirthdayCat.color}22`,
                        color: firstBirthdayCat.color
                      }}
                      title={birthdayEventsForDay.map((b) => b.title).join(', ')}
                    >
                      <Gift size={12} className="flex-shrink-0" />
                      <span className="truncate">
                        {firstBirthday.title}
                        {birthdayEventsForDay.length > 1 && ` +${birthdayEventsForDay.length - 1}`}
                      </span>
                    </div>
                  )}
                </div>

                {/* Reserved space for multi-day bars */}
                {reservedSpace > 0 && (
                  <div style={{ height: reservedSpace }} aria-hidden="true" />
                )}

                {/* Single-day + timed events */}
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 4).map((ev) => {
                    const cat = getCategory(ev.categoryId);
                    const isAllDay = ev.allDay !== false;
                    return (
                      <div
                        key={ev.id}
                        className="text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded font-medium whitespace-nowrap overflow-hidden leading-tight"
                        style={
                          isAllDay
                            ? { backgroundColor: cat?.color, color: '#fff' }
                            : { backgroundColor: `${cat?.color}22`, color: cat?.color }
                        }
                        title={ev.startTime ? `${ev.startTime} ${ev.title}` : ev.title}
                      >
                        {ev.title}
                      </div>
                    );
                  })}
                  {dayEvents.length > 4 && (
                    <div className="text-xs text-gray-500 font-medium px-1">
                      +{dayEvents.length - 4} mehr
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Multi-day bars overlay */}
          {multiDayBars.length > 0 && (
            <div
              className="absolute pointer-events-none"
              style={{
                left: isMobile ? 0 : 50,
                right: 0,
                top: BAR_TOP_OFFSET,
                height: barRowCount * BAR_HEIGHT
              }}
            >
              {multiDayBars.map((bar) => {
                const cat = getCategory(bar.event.categoryId);
                const widthPct = ((bar.endCol - bar.startCol + 1) / 7) * 100;
                const leftPct = (bar.startCol / 7) * 100;
                return (
                  <div
                    key={bar.event.id}
                    className={`absolute text-xs font-medium truncate px-2 flex items-center justify-center ${
                      bar.continuesLeft ? '' : 'rounded-l'
                    } ${bar.continuesRight ? '' : 'rounded-r'}`}
                    style={{
                      left: `calc(${leftPct}% + 2px)`,
                      width: `calc(${widthPct}% - 4px)`,
                      top: bar.row * BAR_HEIGHT,
                      height: BAR_HEIGHT - 2,
                      backgroundColor: cat?.color,
                      color: '#fff'
                    }}
                    title={bar.event.title}
                  >
                    {bar.event.title}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
